import React, { useState, useEffect } from 'react';
import { CheckCircle, Download, FileText, Upload, Calendar, CreditCard, User, X, AlertCircle } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import logo from '../assets/logo.webp';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import axios from 'axios';

const CCAvenueRedirect = ({ msg, url, accessCode }) => {
  const formRef = React.useRef(null);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.submit();
    }
  }, []);

  return (
    <div
      className="d-flex flex-column justify-content-center align-items-center"
      style={{ minHeight: '100vh', background: '#f8f9fa' }}
    >
      <div className="spinner-border text-primary" role="status" />
      <h5 className="text-muted">Redirecting to Payment Gateway...</h5>
      <p className="text-muted small">Please do not close or refresh this page.</p>

      {/* Hidden form — auto-submitted by useEffect above */}
      <form ref={formRef} id="paymentForm" action={url} method="POST" style={{ display: 'none' }}>
        <input type="hidden" name="encRequest" value={msg} />
        {accessCode && <input type="hidden" name="access_code" value={accessCode} />}
      </form>
    </div>
  );
};

const Application = () => {
  const { user } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fees, setFees] = useState([]);
  const [trainees, setTrainees] = useState([]);
  const [applications, setApplications] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [timeSlot, setTimeSlot] = useState('');
  const [shift, setShift] = useState('');
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [appLoading, setAppLoading] = useState(true);
  const [error, setError] = useState(null);
  const { showToast } = useToast();
  const [selectedFiles, setSelectedFiles] = useState({});
  const [mobileNo, setMobileNo] = useState('');
  const [address, setAddress] = useState('');
  const [hasInitialMobile, setHasInitialMobile] = useState(false);
  const [hasInitialAddress, setHasInitialAddress] = useState(false);
  const [paymentData, setPaymentData] = useState(null); // { url, msg } for CCAvenue





  useEffect(() => {
    fetchSessions();
    fetchCategories();
    fetchFees();
    fetchTrainees();
  }, []);

  useEffect(() => {
    if (user) {
      fetchProfile();
      fetchApplications();
    } else {
      setAppLoading(false);
    }
  }, [user]);

  const fetchProfile = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/student-profile`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.status === 'success') {
        const fetchedMobile = data.data.mobile_no || '';
        const fetchedAddress = data.data.address || '';
        setMobileNo(fetchedMobile);
        setAddress(fetchedAddress);
        setHasInitialMobile(!!fetchedMobile);
        setHasInitialAddress(!!fetchedAddress);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    }
  };

  const fetchSessions = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/session-years`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON. Server may be returning an error page.');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setSessions(data.data);
      } else {
        throw new Error('API returned unsuccessful status');
      }
    } catch (err) {
      setError(err.message);
      showToast(err.message, 'error');
      console.error('Error fetching sessions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/categories`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON. Server may be returning an error page.');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setCategories(data.data);
      } else {
        throw new Error('API returned unsuccessful status');
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const fetchFees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/fees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON. Server may be returning an error page.');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setFees(data.data);
      } else {
        throw new Error('API returned unsuccessful status');
      }
    } catch (err) {
      console.error('Error fetching fees:', err);
    }
  };

  const fetchTrainees = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/trainees`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        throw new Error('Response is not JSON. Server may be returning an error page.');
      }
      
      const data = await response.json();
      if (data.status === 'success') {
        setTrainees(data.data);
      } else {
        throw new Error('API returned unsuccessful status');
      }
    } catch (err) {
      console.error('Error fetching trainees:', err);
    }
  };

  const fetchApplications = async () => {
    if (!user) {
      console.log('No user found, skipping fetchApplications');
      setAppLoading(false);
      return;
    }
    setAppLoading(true);
    try {
      console.log('Fetching applications for user:', user.id);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}api/get-swimming-records`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      if (data.status === 'success') {
        console.log('All applications:', data.data);
        console.log('User ID:', user.id);
        const userApplications = data.data.filter(app => app.student_id == user.id);
        console.log('Filtered applications for user:', userApplications);
        setApplications(userApplications);
      } else {
        throw new Error('API returned unsuccessful status');
      }
    } catch (err) {
      console.error('Error fetching applications:', err);
    } finally {
      setAppLoading(false);
    }
  };

  const updateAmount = (sessionId, categoryId) => {
    if (sessionId && categoryId) {
      const feeData = fees.find(fee => 
        fee.session_id.toString() === sessionId && 
        fee.category_id.toString() === categoryId
      );
      if (feeData) {
        setAmount(feeData.fees);
      } else {
        setAmount('');
      }
    } else {
      setAmount('');
    }
  };

  const handleSessionChange = (e) => {
    const sessionId = e.target.value;
    setSelectedSession(sessionId);
    console.log('Session changed:', sessionId, 'Selected Category:', selectedCategory);
    updateAmount(sessionId, selectedCategory);
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    setSelectedCategory(categoryId);
    const selectedCategoryData = categories.find(cat => cat.id.toString() === categoryId);
    if (selectedCategoryData) {
      setTimeSlot(selectedCategoryData.time);
      setShift(selectedCategoryData.shift);
    } else {
      setTimeSlot('');
      setShift('');
    }
    console.log('Category changed:', categoryId, 'Selected Session:', selectedSession);
    updateAmount(selectedSession, categoryId);
  };

  const handleDownload = () => {
    console.log('Downloading declaration...');
  };

  const handleDownloadAdmissionForm = async (app) => {
    // Fetch trainees data
    const fetchTrainees = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/trainees`);
            const data = await response.json();
            if (data.status === 'success') {
                return data.data;
            } else {
                console.error('Failed to fetch trainees:', data.message);
                return [];
            }
        } catch (error) {
            console.error('Error fetching trainees:', error);
            return [];
        }
    };

    const allTrainees = await fetchTrainees();

    // Convert images to base64 to avoid CORS issues
    const getBase64Image = async (url) => {
        if (!url) return '';
        try {
            const response = await fetch(url);
            const blob = await response.blob();
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(reader.result);
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
        } catch (error) {
            console.error('Error converting image to base64:', error);
            return url; // Fallback to original URL if conversion fails
        }
    };

    const logoBase64 = await getBase64Image(logo);
    const photoBase64 = await getBase64Image(app.photo_applicant);
    const signBase64 = await getBase64Image(app.sign_applicant);

    // Create admission form content using provided template
    const formContent = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Admission Form - ${app.application_name}</title>
    <style>
        body {
            font-family: 'Arial', sans-serif;
            margin: 0;
            padding: 0;
            background-color: #f4f4f4;
        }
        .form-container {
            width: 210mm;
            min-height: 297mm;
            margin: 20px auto;
            background: #fff;
            padding: 20mm;
            box-sizing: border-box;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            position: relative;
            border: 1px solid #ddd;
        }
        .header {
            text-align: center;
            margin-bottom: 20px;
            position: relative;
            padding: 0 130px 0 90px;
            min-height: 140px;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
        }
        .logo-circle {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            position: absolute;
            left: 0;
            top: 0;
            overflow: hidden;
        }
        .logo-circle img {
            width: 100%;
            height: auto;
        }
        .photo-box {
            width: 120px;
            height: 140px;
            border: 1px solid #000;
            position: absolute;
            right: 0;
            top: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            text-align: center;
            overflow: hidden;
        }
        .photo-box img {
            max-width: 100%;
            max-height: 100%;
        }
        .title {
            font-size: 18px;
            font-weight: bold;
            margin: 0 0 5px 0;
            text-transform: uppercase;
            line-height: 1.2;
        }
        .subtitle {
            font-size: 16px;
            margin-bottom: 5px;
        }
        .member-type {
            font-size: 18px;
            font-weight: bold;
            margin-top: 10px;
        }
        .field-row {
            margin: 15px 0;
            display: flex;
            align-items: baseline;
        }
        .field-label {
            font-weight: bold;
            margin-right: 10px;
            white-space: nowrap;
        }
        .field-value {
            border-bottom: 1px dotted #000;
            flex-grow: 1;
            padding-left: 5px;
            min-height: 20px;
        }
        table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 20px;
        }
        table, th, td {
            border: 1px solid #000;
        }
        th, td {
            padding: 8px;
            text-align: center;
        }
        .declaration {
            margin-top: 30px;
            font-size: 14px;
            line-height: 1.6;
        }
        .signature-section {
            margin-top: 50px;
            display: flex;
            justify-content: space-between;
            align-items: flex-end;
        }
        .sig-box {
            text-align: center;
            width: 200px;
        }
        .sig-line {
            border-top: 1px solid #000;
            margin-bottom: 5px;
        }
        .print-btn {
            position: fixed;
            bottom: 20px;
            right: 20px;
            padding: 10px 20px;
            background-color: #007bff;
            color: #fff;
            border: none;
            border-radius: 5px;
            cursor: pointer;
            font-size: 16px;
            box-shadow: 0 4px 6px rgba(0,0,0,0.1);
        }
        @media print {
            @page {
                size: A4;
                margin: 0;
            }
            body {
                margin: 0;
                padding: 0;
                background: none;
            }
            .print-btn {
                display: none;
            }
            .form-container {
                width: 210mm;
                height: 297mm;
                margin: 0;
                border: none;
                box-shadow: none;
            }
        }
        .checkbox-row {
            display: flex;
            gap: 20px;
            margin-top: 10px;
            flex-wrap: wrap;
        }
        .checkbox-item {
            display: flex;
            align-items: center;
        }
        .box {
            width: 15px;
            height: 15px;
            border: 1px solid #000;
            margin-right: 5px;
            display: inline-block;
            text-align: center;
            line-height: 15px;
            font-size: 12px;
        }
    </style>
</head>
<body>

    <button class="print-btn" onclick="window.print()">Print / Download PDF</button>

    <div class="form-container">
        <div class="header">
            <div class="logo-circle">
                <img src="${logoBase64}" alt="logo">
            </div>
            <div class="title">BALURGHAT MUNICIPALITY SWIMMING POOL</div>
            <div class="subtitle">Application form for admission in Trainee ${app.session?.session}</div>
            <div class="member-type">Member / Seasonal Swimmer</div>
            <div class="photo-box">
                ${photoBase64 ? `<img src="${photoBase64}" alt="Photo">` : 'PASTE PHOTO HERE'}
            </div>
        </div>

        <div class="field-row">
            <span class="field-label"> Applicant's Name :</span>
            <span class="field-value">${(app.application_name || '').toUpperCase()}</span>
        </div>

        <div class="field-row">
            <span class="field-label">Name of Father / Guardian:</span>
            <span class="field-value">${app.name_of_father || ''}</span>
        </div>

        <div class="field-row">
            <span class="field-label">Residential Address:</span>
            <span class="field-value">${app.residential_address || ''}</span>
        </div>
        <div class="field-row">
            <span class="field-label">Ward No:</span>
            <span class="field-value">${app.ward_no || ''}</span>
            <span class="field-label" style="margin-left: 20px;">Mob. no:</span>
            <span class="field-value">${app.mobile_no || ''}</span>
        </div>

        <div class="field-row">
            <span class="field-label"> Date of Birth:</span>
            <span class="field-value">${app.date_of_birth ? new Date(app.date_of_birth).toLocaleDateString('en-GB') : ''}</span>
            <span class="field-label" style="margin-left: 20px;">Height:</span>
            <span class="field-value">${app.height || ''}</span>
        </div>

        <div class="checkbox-row">
            ${allTrainees.map(trainee => {
                const isSelected = app.trainee_ids && app.trainee_ids.includes(trainee.id.toString());
                return `
                    <div class="checkbox-item">
                        <span class="field-label">${trainee.traine_name}:</span>
                        <div class="box">${isSelected ? '✔' : 'X'}</div>
                    </div>
                `;
            }).join('')}
        </div>

        <table>
            <thead>
                <tr>
                    <th>CATEGORY</th>
                    <th>SHIFT</th>
                    <th>TIME</th>
                </tr>
            </thead>
            <tbody>
                <tr>
                    <td>${app.category?.category || 'N/A'}</td>
                    <td>${app.shift?.shift_name || 'N/A'}</td>
                    <td>${app.time_slot?.time || 'N/A'}</td>
                </tr>
            </tbody>
        </table>

        <div class="field-row" style="margin-top: 30px;">
            <span class="field-label">Subscription Rs.</span>
            <span class="field-value">${app.subscription_amount ? Number(app.subscription_amount).toFixed(2) : '0.00'}</span>
        </div>

        <div class="declaration">
            I shall abide by all rules and regulation of Municipality authority failing which my membership can be transmitted.
        </div>

        <div class="signature-section" style="margin-top: 30px;">
            <div class="sig-box" style="text-align: left;">
                <div class="field-row" style="margin-bottom: 10px;">
                    <span class="field-label">Date:</span>
                    <span class="field-value">${app.registration_date ? new Date(app.registration_date).toLocaleDateString('en-GB') : ''}</span>
                </div>
            </div>

            <div class="sig-box">
               <div style="margin-top: 20px;">
                    ${signBase64 ? 
                        `<img src="${signBase64}" alt="Signature" style="max-height: 40px; margin-bottom: 5px;">` : 
                        '<div style="height: 60px; border-bottom: 1px dotted #000; width: 150px; margin-bottom: 5px;"></div>'
                    }
                    <div style="border-top: 1px solid #000; width: 200px; margin-left: auto; margin-right: auto;"></div>
                    <div style="font-weight: 200; margin-bottom: 5px;">Applicant Signature</div>
                </div>
            </div>
        </div>
        
    </div>

    <script>
        // Auto-trigger print dialog when page loads
        window.onload = function() {
            setTimeout(() => {
                window.print();
            }, 500);
        };
    </script>
</body>
</html>
    `;

    // Open in new window and trigger print
    const printWindow = window.open('', '_blank');
    printWindow.document.write(formContent);
    printWindow.document.close();
    
    // Focus on new window
    printWindow.focus();
  };

  const handleFileUploadClick = (inputId) => {
    const fileInput = document.getElementById(inputId);
    if (fileInput) {
      fileInput.click();
    }
  };

  const handleFileChange = (event, fieldName) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFiles(prev => ({
        ...prev,
        [fieldName]: file.name
      }));
    }
  };



  const validateForm = (formData) => {
    const errors = [];
    
    // Check required fields
    if (!selectedSession) errors.push('Please select a session');
    if (!selectedCategory) errors.push('Please select a category');
    if (!formData.get('application_name')) errors.push('Application name is required');
    if (!formData.get('name_of_father')) errors.push('Father\'s name is required');
    if (!formData.get('residential_address')) errors.push('Residential address is required');
    if (!formData.get('ward_no')) errors.push('Ward number is required');
    if (!formData.get('date_of_birth')) errors.push('Date of birth is required');
    if (!formData.get('height')) errors.push('Height is required');
    if (!formData.get('mobile_no')) errors.push('Mobile number is required');
    if (!formData.get('gender')) errors.push('Gender is required');
    
    // Check trainee selection
    const selectedTrainee = document.querySelector('input[name="traineeManagement"]:checked');
    if (!selectedTrainee) errors.push('Please select trainee management type');
    
    // Check file uploads
    if (!selectedFiles.sign_applicant) errors.push('Sign of applicant is required');
    if (!selectedFiles.photo_applicant) errors.push('Photo of applicant is required');
    if (!selectedFiles.declaration_by_father) errors.push('Declaration by father is required');
    if (!selectedFiles.medical_certificate) errors.push('Medical certificate is required');
    
    return errors;
  };

  const initiatePayment = async (formData) => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Check capacity before proceeding to payment
      const capacityRes = await axios.post(
        `${process.env.REACT_APP_API_URL}api/check-capacity`,
        {
          session_id: parseInt(selectedSession),
          category_id: parseInt(selectedCategory)
        }
      );

      if (capacityRes.data.status === 'error' || capacityRes.data.remaining_capacity <= 0) {
        showToast('Sorry, this session and category is now full. Please choose another one.', 'error');
        setPaymentLoading(false);
        return;
      }
      
      const paymentDataPayload = {
        order_id: "ORD_" + Date.now(),
        amount: amount ? amount.toString() : "500.00",
        currency: "INR",
        billing_name: formData.get('application_name') || user?.name || 'User',
        billing_email: formData.get('email') || user?.email || 'user@example.com',
        billing_tel: formData.get('mobile_no') || user?.mobile_no || '0000000000'
      };

      // Store form data for payment success callback
      sessionStorage.setItem('pendingApplication', JSON.stringify(Object.fromEntries(formData)));
      
      // Get IDs for student-payment API from categories/fees
      const catInfo = categories.find(cat => cat.id.toString() === selectedCategory);
      const feeInfo = fees.find(f => f.session_id.toString() === selectedSession && f.category_id.toString() === selectedCategory);

      // Store IDs for student-payment API
      sessionStorage.setItem('pendingPaymentInfo', JSON.stringify({
        session_id: parseInt(selectedSession),
        category_id: parseInt(selectedCategory),
        shift_id: parseInt(catInfo?.shift_id || feeInfo?.shift_id || formData.get('traineeManagement')),
        time_slot_id: parseInt(catInfo?.time_slot_id || feeInfo?.time_slot_id || 1),
        amount: amount,
        isNew: true
      }));

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}api/ccavenue/request-handler`,
        paymentDataPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('CCAvenue API Response type:', typeof res.data);
      console.log('CCAvenue API Response:', res.data);

      // 1. HANDLE HTML RESPONSE (Like the one in your Postman screenshot)
      if (typeof res.data === 'string' && res.data.includes('<form')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(res.data, 'text/html');
        const serverForm = doc.querySelector('form');
        
        if (serverForm) {
          // Create a real form in our document and submit it
          const realForm = document.createElement('form');
          realForm.method = serverForm.method || 'POST';
          realForm.action = serverForm.action;
          realForm.style.display = 'none';
          
          // Copy all hidden inputs (encRequest, access_code, etc.)
          serverForm.querySelectorAll('input').forEach(input => {
            const newInput = document.createElement('input');
            newInput.type = 'hidden';
            newInput.name = input.name;
            newInput.value = input.value;
            realForm.appendChild(newInput);
          });
          
          document.body.appendChild(realForm);
          realForm.submit();
          return;
        }
      }

      // 2. HANDLE JSON RESPONSE
      const paymentUrl = res.data.payment_url || res.data.url;
      const paymentMsg = res.data.encRequest || res.data.msg || res.data.encrypted_data;
      const accessCode = res.data.access_code;

      if (paymentUrl && paymentMsg) {
        setPaymentData({ url: paymentUrl, msg: paymentMsg, accessCode: accessCode });
      } else {
        throw new Error('Invalid payment response: could not find form or redirect data');
      }

    } catch (error) {
      console.error('Payment initiation error:', error);
      showToast('Failed to initiate payment. Please try again.', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleExistingPayment = async (app) => {
    setPaymentLoading(true);
    try {
      const token = localStorage.getItem('token');

      const sId = app.session?.id || app.session_id;
      const cId = app.category?.id || app.category_id;
      const shId = app.shift?.id || app.shift_id || (app.category?.shift_id);
      const tsId = app.time_slot?.id || app.time_slot_id || (app.category?.time_id);

      if (!sId || !cId) {
        console.error('Missing session or category ID in app record:', app);
        showToast('Application data is incomplete. Please contact support.', 'error');
        setPaymentLoading(false);
        return;
      }

      // Check capacity before proceeding to payment
      const capacityRes = await axios.post(
        `${process.env.REACT_APP_API_URL}api/check-capacity`,
        {
          session_id: parseInt(sId),
          category_id: parseInt(cId)
        }
      );

      if (capacityRes.data.status === 'error' || capacityRes.data.remaining_capacity <= 0) {
        showToast('Sorry, this session and category is now full.', 'error');
        setPaymentLoading(false);
        return;
      }
      
      const paymentDataPayload = {
        order_id: `ORD_${Date.now()}_${app.id}`,
        amount: (app.subscription_amount || "500").toString(),
        currency: "INR",
        billing_name: app.application_name || user?.name || 'User',
        billing_email: user?.email || 'user@example.com',
        billing_tel: app.mobile_no || user?.mobile_no || '0000000000'
      };

      // Store IDs for student-payment API
      sessionStorage.setItem('pendingPaymentInfo', JSON.stringify({
        session_id: parseInt(sId),
        category_id: parseInt(cId),
        shift_id: parseInt(shId || 1),
        time_slot_id: parseInt(tsId || 1),
        amount: app.subscription_amount,
        application_id: app.id,
        isNew: false
      }));

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}api/ccavenue/request-handler`,
        paymentDataPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Payment API Response:', res.data);

      // 1. HANDLE HTML RESPONSE
      if (typeof res.data === 'string' && res.data.includes('<form')) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(res.data, 'text/html');
        const serverForm = doc.querySelector('form');
        
        if (serverForm) {
          const realForm = document.createElement('form');
          realForm.method = serverForm.method || 'POST';
          realForm.action = serverForm.action;
          realForm.style.display = 'none';
          
          serverForm.querySelectorAll('input').forEach(input => {
            const newInput = document.createElement('input');
            newInput.type = 'hidden';
            newInput.name = input.name;
            newInput.value = input.value;
            realForm.appendChild(newInput);
          });
          
          document.body.appendChild(realForm);
          realForm.submit();
          return;
        }
      }

      // 2. HANDLE JSON RESPONSE
      const paymentUrl = res.data.payment_url || res.data.url;
      const paymentMsg = res.data.encRequest || res.data.msg || res.data.encrypted_data;
      const accessCode = res.data.access_code;

      if (paymentUrl && paymentMsg) {
        setPaymentData({ url: paymentUrl, msg: paymentMsg, accessCode: accessCode });
      } else {
        throw new Error('Invalid payment response: could not find form or redirect data');
      }

    } catch (error) {
      console.error('Payment error:', error);
      showToast('Failed to initiate payment. Please try again.', 'error');
    } finally {
      setPaymentLoading(false);
    }
  };


  const createApplication = async (formData) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Check capacity before proceeding with registration
      const capacityRes = await axios.post(
        `${process.env.REACT_APP_API_URL}api/check-capacity`,
        {
          session_id: parseInt(selectedSession),
          category_id: parseInt(selectedCategory)
        }
      );

      if (capacityRes.data.data.no_of_seat === 0) {
        showToast('Sorry, no seats available.', 'error');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL}api/swimming-registration`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status === 'success') {
        showToast('Application submitted successfully!', 'success');
        setShowForm(false);
        fetchApplications();
      } else {
        // Use the message from the API if available, otherwise fallback to a generic message
        const errorMessage = result.message || 'Application submission failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Application submission error:', error);
      showToast(error.message || 'Failed to submit application. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Create FormData
    const formData = new FormData(e.target);
    
    // Validate form
    const errors = validateForm(formData);
    if (errors.length > 0) {
      errors.forEach(error => showToast(error));
      return;
    }

    // Add required fields
    formData.set('session_id', selectedSession);
    formData.set('category_id', selectedCategory);
    formData.set('payment_type', 'online');

    // Get selected trainee
    const selectedTrainee = document.querySelector('input[name="traineeManagement"]:checked');
    if (selectedTrainee) {
      formData.set('trainee_ids', selectedTrainee.value);
    }

    // Handle file uploads properly - get actual file objects
    const signApplicantInput = document.getElementById('signApplicantInput');
    const photoApplicantInput = document.getElementById('photoApplicantInput');
    const declarationFatherInput = document.getElementById('declarationFatherInput');
    const medicalCertificateInput = document.getElementById('medicalCertificateInput');

    if (signApplicantInput?.files[0]) {
      formData.set('sign_applicant', signApplicantInput.files[0]);
    }
    if (photoApplicantInput?.files[0]) {
      formData.set('photo_applicant', photoApplicantInput.files[0]);
    }
    if (declarationFatherInput?.files[0]) {
      formData.set('declaration_by_father', declarationFatherInput.files[0]);
    }
    if (medicalCertificateInput?.files[0]) {
      formData.set('medical_certificate', medicalCertificateInput.files[0]);
    }

    // Capture gender specifically if needed (though FormData(e.target) should handle it)
    const selectedGender = document.querySelector('input[name="gender"]:checked');
    if (selectedGender) {
      formData.set('gender', selectedGender.value);
    }

    // Submit application directly
    await createApplication(formData);
  };

  const checklistItems = [
    { id: 1, text: "Passport size photograph (Recent, color)", type: "image" },
    { id: 2, text: "Birth certificate", type: "document" },
    { id: 3, text: "Medical fitness certificate", type: "medical" },
    { id: 4, text: "Parent's ID proof (for minors)", type: "id" },
    { id: 5, text: "Address proof", type: "address" },
    { id: 6, text: "Age: Minimum 5 years", type: "age" },
    { id: 7, text: "Basic swimming knowledge required", type: "skill" },
    { id: 8, text: "Parental consent for minors", type: "consent" }
  ];

  const renderIcon = (type) => {
    const iconProps = { size: 20 };
    switch (type) {
      case "image":
        return <User {...iconProps} className="text-primary" />;
      case "document":
        return <FileText {...iconProps} className="text-success" />;
      case "medical":
        return <CheckCircle {...iconProps} className="text-warning" />;
      case "id":
        return <CreditCard {...iconProps} className="text-info" />;
      case "address":
        return <FileText {...iconProps} className="text-secondary" />;
      case "age":
        return <Calendar {...iconProps} className="text-danger" />;
      case "skill":
        return <CheckCircle {...iconProps} className="text-primary" />;
      case "consent":
        return <CheckCircle {...iconProps} className="text-success" />;
      default:
        return <FileText {...iconProps} className="text-muted" />;
    }
  };

  if (showForm) {
    return (
      <div className="dashboard-container">
        
        <Header />
        <div className="dashboard-wrapper">
          <Sidebar activeItem="application" />
          <div className="flex-grow-1 dashboard-content">
            <h2 className="page-title">Application Form</h2>
            <div className="application-form-card">
              <form onSubmit={handleSubmit}>
                <div className="form-section">
                  <h4 className="form-section-title">Basic Information</h4>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Session <span className="required-star">*</span>
                      </label>
                      <select className="enhanced-form-control" onChange={handleSessionChange} value={selectedSession}>
                        <option value="">Select Session</option>
                        {loading && <option value="">Loading sessions...</option>}
                        {error && <option value="">Error loading sessions</option>}
                        {!loading && !error && sessions.map((session) => (
                          <option key={session.id} value={session.id}>
                            {session.session} {session.current_session === 1 ? '(Current)' : ''}
                          </option>
                        ))}
                      </select>
                      {error && (
                        <div className="text-danger small mt-1">
                          Failed to load sessions: {error}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Category <span className="required-star">*</span>
                      </label>
                      <select className="enhanced-form-control" onChange={handleCategoryChange} value={selectedCategory}>
                        <option value="">Select Category</option>
                        {categories.length === 0 ? (
                          <option value="">Loading categories...</option>
                        ) : (
                          categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.category} ({category.shift} - {category.time})
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Application Name <span className="required-star">*</span>
                      </label>
                      <input 
                        type="text" 
                        className="enhanced-form-control" 
                        name="application_name" 
                        value={user?.name || ''}
                        readOnly
                        style={{ backgroundColor: '#eeeeee', cursor: 'not-allowed' }}
                        placeholder="Enter your name" 
                      />
                      <small className="text-muted mt-1"> (This name using every where in application)</small>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Father's Name <span className="required-star">*</span>
                      </label>
                      <input type="text" className="enhanced-form-control" name="name_of_father" placeholder="Enter Father's Name" />
                    </div>
                    <div className="col-12 mb-3">
                      <label className="enhanced-form-label">
                        Residential Address <span className="required-star">*</span>
                      </label>
                      <textarea 
                        className="enhanced-form-control" 
                        name="residential_address" 
                        rows="3" 
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        readOnly={hasInitialAddress}
                        style={hasInitialAddress ? { backgroundColor: '#eeeeee', cursor: 'not-allowed' } : {}}
                        placeholder="Enter Residential Address"
                      ></textarea>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Ward No <span className="required-star">*</span>
                      </label>
                      <input type="text" className="enhanced-form-control" name="ward_no" placeholder="Enter Ward No" />
                    </div>
                  </div>
                </div>

                <div className="form-section">
                  <h4 className="form-section-title">Personal Details</h4>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Date of Birth <span className="required-star">*</span>
                      </label>
                      <input type="date" className="enhanced-form-control" name="date_of_birth" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Height <span className="required-star">*</span>
                      </label>
                      <input type="text" className="enhanced-form-control" name="height" placeholder="Enter Height (e.g. 5'6&quot;)" />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">
                        Mobile No <span className="required-star">*</span>
                      </label>
                      <input 
                        type="tel" 
                        className="enhanced-form-control" 
                        name="mobile_no" 
                        value={mobileNo}
                        onChange={(e) => setMobileNo(e.target.value)}
                        readOnly={hasInitialMobile}
                        style={hasInitialMobile ? { backgroundColor: '#eeeeee', cursor: 'not-allowed' } : {}}
                        placeholder="Enter Mobile No" 
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">Gender <span className="required-star">*</span></label>
                      <div className="d-flex gap-4 mt-2">
                        <div className="form-check">
                          <input className="form-check-input" type="radio" name="gender" id="genderMale" value="male" />
                          <label className="form-check-label" htmlFor="genderMale">Male</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" name="gender" id="genderFemale" value="female" />
                          <label className="form-check-label" htmlFor="genderFemale">Female</label>
                        </div>
                        <div className="form-check">
                          <input className="form-check-input" type="radio" name="gender" id="genderOther" value="other" />
                          <label className="form-check-label" htmlFor="genderOther">Other</label>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">Trainee Management</label>
                      <div className="checkbox-group">
                        {trainees.map((trainee) => (
                          <div className="form-check" key={trainee.id}>
                            <input 
                              className="enhanced-checkbox" 
                              type="radio" 
                              name="traineeManagement" 
                              id={`trainee${trainee.id}`}
                              value={trainee.id}
                            />
                            <label className="form-check-label" htmlFor={`trainee${trainee.id}`}>
                              {trainee.traine_name}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">Shift</label>
                      <input type="text" className="enhanced-form-control" name="shift" value={shift} readOnly />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">Time Slot</label>
                      <input type="text" className="enhanced-form-control" name="time_slot" value={timeSlot} placeholder="Time Slot" readOnly />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="enhanced-form-label">Amount</label>
                      <input type="text" className="enhanced-form-control" name="amount" value={amount} placeholder="Amount" readOnly />
                    </div>
                  </div>
                </div>

                <div className="file-upload-section">
                  <h4 className="form-section-title">Document Uploads</h4>
                  <div className="file-upload-grid">
                    <div className="file-upload-item" onClick={() => handleFileUploadClick('signApplicantInput')}>
                      <div className="file-upload-icon">
                        <User size={20} />
                      </div>
                      <div className="file-upload-label">Sign of Applicant *</div>
                      <input 
                        type="file" 
                        id="signApplicantInput" 
                        className="enhanced-form-control" 
                        name="sign_applicant" 
                        accept="image/*" 
                        style={{display: "none"}} 
                        onChange={(e) => handleFileChange(e, 'sign_applicant')}
                      />
                      <div className="text-muted small mt-2">
                        {selectedFiles.sign_applicant || 'Click to upload'}
                      </div>
                    </div>
                    <div className="file-upload-item" onClick={() => handleFileUploadClick('photoApplicantInput')}>
                      <div className="file-upload-icon">
                        <User size={20} />
                      </div>
                      <div className="file-upload-label">Photo of Applicant *</div>
                      <input 
                        type="file" 
                        id="photoApplicantInput" 
                        className="enhanced-form-control" 
                        name="photo_applicant" 
                        accept="image/*" 
                        style={{display: "none"}} 
                        onChange={(e) => handleFileChange(e, 'photo_applicant')}
                      />
                      <div className="text-muted small mt-2">
                        {selectedFiles.photo_applicant || 'Click to upload'}
                      </div>
                    </div>
                    <div className="file-upload-item" onClick={() => handleFileUploadClick('declarationFatherInput')}>
                      <div className="file-upload-icon">
                        <FileText size={20} />
                      </div>
                      <div className="file-upload-label">Declaration by Father *</div>
                      <input 
                        type="file" 
                        id="declarationFatherInput" 
                        className="enhanced-form-control" 
                        name="declaration_by_father" 
                        accept="image/*,.pdf" 
                        style={{display: "none"}} 
                        onChange={(e) => handleFileChange(e, 'declaration_by_father')}
                      />
                      <div className="text-muted small mt-2">
                        {selectedFiles.declaration_by_father || 'Click to upload'}
                      </div>
                    </div>
                    <div className="file-upload-item" onClick={() => handleFileUploadClick('medicalCertificateInput')}>
                      <div className="file-upload-icon">
                        <CheckCircle size={20} />
                      </div>
                      <div className="file-upload-label">Medical Certificate *</div>
                      <input 
                        type="file" 
                        id="medicalCertificateInput" 
                        className="enhanced-form-control" 
                        name="medical_certificate" 
                        accept="image/*,.pdf" 
                        style={{display: "none"}} 
                        onChange={(e) => handleFileChange(e, 'medical_certificate')}
                      />
                      <div className="text-muted small mt-2">
                        {selectedFiles.medical_certificate || 'Click to upload'}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="form-actions">
                  <button type="button" className="enhanced-btn btn-back" onClick={() => setShowForm(false)}>
                    Back
                  </button>
                  <button type="submit" className="enhanced-btn btn-submit" disabled={loading}>
                    {loading ? 'Submitting...' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (paymentData) {
    return <CCAvenueRedirect url={paymentData.url} msg={paymentData.msg} accessCode={paymentData.accessCode} />;
  }

  return (
    <div className="dashboard-container">
      <Header />
      
      {/* Toast Notifications */}


      {/* Error Toast Notification rendering has been removed to avoid duplicate notifications */}

    <div className="dashboard-wrapper">
      <Sidebar activeItem="application" />
      <div className="flex-grow-1 dashboard-content">
        <div className="application-header">
          <h2 className="application-title">Swimming Training Application</h2>
          <p className="application-subtitle">Complete your application for swimming training at Balurghat Municipality Swimming Pool</p>
        </div>
        
        {appLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : applications.length > 0 ? (
          <div className="application-list">
            <h3 className="page-title mb-4">Your Applications</h3>
            <div className="application-cards-container">
              {applications.map((app) => (
                <div key={app.id} className="card shadow-sm border-0 mb-4 rounded-4 overflow-hidden" style={{ borderRadius: '1rem' }}>
                  <div className="card-header bg-light border-bottom p-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '50px', height: '50px', backgroundColor: '#e2edff' }}>
                        <User size={24} style={{ color: '#0d6efd' }} />
                      </div>
                      <div>
                        <h4 className="mb-0 fw-bold text-dark">{app.application_name}</h4>
                        <div className="text-muted d-flex align-items-center gap-2 mt-1 flex-wrap">
                          <span className="small">Reg No: <strong>{app.registration_no || 'Pending'}</strong></span>
                          <span className="text-secondary small">•</span>
                          <span className="small">Session: <strong>{app.session?.session || 'N/A'}</strong></span>
                        </div>
                      </div>
                    </div>
                    <div className="align-self-start align-self-md-center">
                      <span className={`badge rounded-pill px-3 py-2 ${app.payment_status == 1 ? 'bg-success' : 'bg-warning text-dark'}`} style={{ fontSize: '13px' }}>
                        {app.payment_status == 1 ? 'Active' : 'Pending Payment'}
                      </span>
                    </div>
                  </div>
                  <div className="card-body p-4">
                    <div className="row g-4">
                      <div className="col-md-3 col-6">
                        <div className="text-muted small mb-1 fw-medium text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Father's Name</div>
                        <div className="fw-semibold text-dark fs-6 text-truncate" title={app.name_of_father}>{app.name_of_father}</div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="text-muted small mb-1 fw-medium text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Category</div>
                        <div className="fw-semibold text-dark fs-6 text-truncate" title={app.category?.category || 'N/A'}>{app.category?.category || 'N/A'}</div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="text-muted small mb-1 fw-medium text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Registration Date</div>
                        <div className="fw-semibold text-dark fs-6">{new Date(app.registration_date).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                      </div>
                      <div className="col-md-3 col-6">
                        <div className="text-muted small mb-1 fw-medium text-uppercase" style={{ letterSpacing: '0.5px', fontSize: '11px' }}>Schedule</div>
                        <div className="fw-semibold text-dark fs-6 mb-1">{app.shift?.shift_name || 'N/A'} Shift</div>
                        <div className="badge bg-light text-dark border fw-normal">{app.time_slot?.time || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="mt-3 d-flex flex-wrap gap-2">
                      {app.payment_status == 1 ? (
                        <button 
                          className="enhanced-btn btn-outline-primary btn-sm d-flex align-items-center gap-2"
                          onClick={() => handleDownloadAdmissionForm(app)}
                        >
                          <Download size={16} />
                          Download Admission Form
                        </button>
                      ) : (
                        <div className="w-100 mt-2">
                          <div className="payment-alert p-3 rounded-4 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3" style={{ backgroundColor: '#fff8eb', border: '1px border-warning' }}>
                            <div className="d-flex align-items-center gap-3">
                              <div className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0" style={{ width: '40px', height: '40px', backgroundColor: '#fff1d6' }}>
                                <AlertCircle size={20} className="text-warning" />
                              </div>
                              <div>
                                <h6 className="mb-1 fw-bold text-dark">Required: Complete Your Payment</h6>
                                <p className="mb-0 text-muted small">Subscription Fee: <strong>₹{app.subscription_amount}</strong> for Session: <strong>{app.session?.session || 'N/A'}</strong>. Pay now to activate your application.</p>
                              </div>
                            </div>
                            <button 
                              className="btn btn-primary px-4 py-2 d-flex align-items-center gap-2 fw-bold shadow-sm"
                              onClick={() => handleExistingPayment(app)}
                              disabled={paymentLoading}
                              style={{ borderRadius: '10px' }}
                            >
                              <CreditCard size={18} />
                              {paymentLoading ? 'Processing...' : `Pay ₹${app.subscription_amount}`}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {applications.some(app => app.payment_status == 1) && (
              <div className="action-section mt-5 border-top pt-4">
                <div className="alert mb-4 border-0 d-flex align-items-center gap-3" style={{ backgroundColor: '#e8f4fd', borderRadius: '12px', padding: '16px' }}>
                  <div style={{ color: '#0d6efd' }}><AlertCircle size={24} /></div>
                  <div>
                    <strong className="d-block text-dark mb-1">Registration Complete</strong>
                    <span className="text-secondary small">Your registration is active. You can download your form above or manage your sessions.</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          // Show the actual application form directly for users with no applications
          <div className="application-form-card">
            <form onSubmit={handleSubmit}>
              <div className="form-section">
                <h4 className="form-section-title">Basic Information</h4>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Session <span className="required-star">*</span>
                    </label>
                    <select className="enhanced-form-control" onChange={handleSessionChange} value={selectedSession}>
                      <option value="">Select Session</option>
                      {loading && <option value="">Loading sessions...</option>}
                      {error && <option value="">Error loading sessions</option>}
                      {!loading && !error && sessions.map((session) => (
                        <option key={session.id} value={session.id}>
                          {session.session} {session.current_session === 1 ? '(Current)' : ''}
                        </option>
                      ))}
                    </select>
                    {error && (
                      <div className="text-danger small mt-1">
                        Failed to load sessions: {error}
                      </div>
                    )}
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Category <span className="required-star">*</span>
                    </label>
                    <select className="enhanced-form-control" onChange={handleCategoryChange} value={selectedCategory}>
                      <option value="">Select Category</option>
                      {categories.length === 0 ? (
                        <option value="">Loading categories...</option>
                      ) : (
                        categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.category} ({category.shift} - {category.time})
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Application Name <span className="required-star">*</span>
                    </label>
                    <input 
                      type="text" 
                      className="enhanced-form-control" 
                      name="application_name" 
                      value={user?.name || ''}
                      readOnly
                      style={{ backgroundColor: '#eeeeee', cursor: 'not-allowed' }}
                      placeholder="Enter your name" 
                    />
                    <small className="text-muted mt-1"> (This name using every where in application)</small>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Father's Name <span className="required-star">*</span>
                    </label>
                    <input type="text" className="enhanced-form-control" name="name_of_father" placeholder="Enter Father's Name" />
                  </div>
                  <div className="col-12 mb-3">
                    <label className="enhanced-form-label">
                      Residential Address <span className="required-star">*</span>
                    </label>
                    <textarea 
                      className="enhanced-form-control" 
                      name="residential_address" 
                      rows="3" 
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      readOnly={hasInitialAddress}
                      style={hasInitialAddress ? { backgroundColor: '#eeeeee', cursor: 'not-allowed' } : {}}
                      placeholder="Enter Residential Address"
                    ></textarea>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Ward No <span className="required-star">*</span>
                    </label>
                    <input type="text" className="enhanced-form-control" name="ward_no" placeholder="Enter Ward No" />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h4 className="form-section-title">Personal Details</h4>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Date of Birth <span className="required-star">*</span>
                    </label>
                    <input type="date" className="enhanced-form-control" name="date_of_birth" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Height <span className="required-star">*</span>
                    </label>
                    <input type="text" className="enhanced-form-control" name="height" placeholder="Enter Height (e.g. 5'6&quot;)" />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">
                      Mobile No <span className="required-star">*</span>
                    </label>
                    <input 
                      type="tel" 
                      className="enhanced-form-control" 
                      name="mobile_no" 
                      value={mobileNo}
                      onChange={(e) => setMobileNo(e.target.value)}
                      readOnly={hasInitialMobile}
                      style={hasInitialMobile ? { backgroundColor: '#eeeeee', cursor: 'not-allowed' } : {}}
                      placeholder="Enter Mobile No" 
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">Gender <span className="required-star">*</span></label>
                    <div className="d-flex gap-4 mt-2">
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="gender" id="genderMaleMain" value="male" />
                        <label className="form-check-label" htmlFor="genderMaleMain">Male</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="gender" id="genderFemaleMain" value="female" />
                        <label className="form-check-label" htmlFor="genderFemaleMain">Female</label>
                      </div>
                      <div className="form-check">
                        <input className="form-check-input" type="radio" name="gender" id="genderOtherMain" value="other" />
                        <label className="form-check-label" htmlFor="genderOtherMain">Other</label>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">Trainee Management</label>
                    <div className="checkbox-group">
                      {trainees.map((trainee) => (
                        <div className="form-check" key={trainee.id}>
                          <input 
                            className="enhanced-checkbox" 
                            type="radio" 
                            name="traineeManagement" 
                            id={`trainee${trainee.id}`}
                            value={trainee.id}
                          />
                          <label className="form-check-label" htmlFor={`trainee${trainee.id}`}>
                            {trainee.traine_name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">Shift</label>
                    <input type="text" className="enhanced-form-control" name="shift" value={shift} readOnly />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">Time Slot</label>
                    <input type="text" className="enhanced-form-control" name="time_slot" value={timeSlot} placeholder="Time Slot" readOnly />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="enhanced-form-label">Amount</label>
                    <input type="text" className="enhanced-form-control" name="amount" value={amount} placeholder="Amount" readOnly />
                  </div>
                </div>
              </div>

              <div className="file-upload-section">
                <h4 className="form-section-title">Document Uploads</h4>
                <div className="file-upload-grid">
                  <div className="file-upload-item" onClick={() => handleFileUploadClick('signApplicantInput')}>
                    <div className="file-upload-icon">
                      <User size={20} />
                    </div>
                    <div className="file-upload-label">Sign of Applicant *</div>
                    <input 
                      type="file" 
                      id="signApplicantInput" 
                      className="enhanced-form-control" 
                      name="sign_applicant" 
                      accept="image/*" 
                      style={{display: "none"}} 
                      onChange={(e) => handleFileChange(e, 'sign_applicant')}
                    />
                    <div className="text-muted small mt-2">
                      {selectedFiles.sign_applicant || 'Click to upload'}
                    </div>
                  </div>
                  <div className="file-upload-item" onClick={() => handleFileUploadClick('photoApplicantInput')}>
                    <div className="file-upload-icon">
                      <User size={20} />
                    </div>
                    <div className="file-upload-label">Photo of Applicant *</div>
                    <input 
                      type="file" 
                      id="photoApplicantInput" 
                      className="enhanced-form-control" 
                      name="photo_applicant" 
                      accept="image/*" 
                      style={{display: "none"}} 
                      onChange={(e) => handleFileChange(e, 'photo_applicant')}
                    />
                    <div className="text-muted small mt-2">
                      {selectedFiles.photo_applicant || 'Click to upload'}
                    </div>
                  </div>
                  <div className="file-upload-item" onClick={() => handleFileUploadClick('declarationFatherInput')}>
                    <div className="file-upload-icon">
                      <FileText size={20} />
                    </div>
                    <div className="file-upload-label">Declaration by Father *</div>
                    <input 
                      type="file" 
                      id="declarationFatherInput" 
                      className="enhanced-form-control" 
                      name="declaration_by_father" 
                      accept="image/*,.pdf" 
                      style={{display: "none"}} 
                      onChange={(e) => handleFileChange(e, 'declaration_by_father')}
                    />
                    <div className="text-muted small mt-2">
                      {selectedFiles.declaration_by_father || 'Click to upload'}
                    </div>
                  </div>
                  <div className="file-upload-item" onClick={() => handleFileUploadClick('medicalCertificateInput')}>
                    <div className="file-upload-icon">
                      <CheckCircle size={20} />
                    </div>
                    <div className="file-upload-label">Medical Certificate *</div>
                    <input 
                      type="file" 
                      id="medicalCertificateInput" 
                      className="enhanced-form-control" 
                      name="medical_certificate" 
                      accept="image/*,.pdf" 
                      style={{display: "none"}} 
                      onChange={(e) => handleFileChange(e, 'medical_certificate')}
                    />
                    <div className="text-muted small mt-2">
                      {selectedFiles.medical_certificate || 'Click to upload'}
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="form-actions">
                <button type="submit" className="enhanced-btn btn-submit" disabled={loading}>
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        )}

      </div>
    </div>
  </div>
  );
};

export default Application;
